import { Component, createRef } from "react"
import { stringify } from "querystringify"

import { BookingStatus, BookingStatusNameMap, PaymentSystemType } from "utils/interfaces"
import { SESSION_TYPE } from "modules/Booking/common/constants"
import * as utils from "utils/utils"
import { SentryWrapper } from "utils/ThirdParty/Sentry/SentryWrapper"
import { httpClient } from "utils/infrastructure/httpClient"

interface Props {
    payment_system: PaymentSystemType | undefined
    onError(message: string): void
    initUrl?: string
    order_id?: string | number
    onWidgetShown?: () => void
    booking_hash?: string
    sessionType?: SESSION_TYPE
    client_id?: number
    status?: string
    psy_profile_url?: string
    psy_full_name?: string
    psyHashId?: string
    returnUrl?: string
    onInit?: (prop: string) => void
    onAfterPayment?: () => void
    savePaymentTool?: boolean
}

interface InitPaymentResponse {
    error?: string
    payment_id: string
    confirmation_token: string
}

interface JsonData {
    booking_hash?: string
    payment_system: PaymentSystemType | undefined
    force_init?: boolean
    save_payment_tool?: boolean
}

interface YookassaWidgetError {
    error: string
}

interface YooWidget {
    render(id?: string): void
    destroy(): void
    on: (name: string, callback: () => void) => void
}

declare global {
    interface Window {
        YooMoneyCheckoutWidget: YooWidgetConstructor
    }
}

interface YooWidgetConstructor {
    new (args: {
        customization: {
            colors: { controlPrimary: string; controlPrimaryContent: string }
            payment_methods?: string[]
        }
        error_callback: (error: YookassaWidgetError) => Promise<void> | undefined
        return_url: string
        confirmation_token: string
    }): YooWidget
}

export class YooKassaWidget extends Component<Props> {
    widgetScript: HTMLScriptElement | undefined
    widget: YooWidget | undefined
    widgetRef = createRef<HTMLDivElement>()

    constructor(props: Props) {
        super(props)
        if (this.widgetScript !== undefined) this.widgetScript.remove()

        this.widgetScript = utils.loadScript(
            "https://yookassa.ru/checkout-widget/v1/checkout-widget.js",
            () =>
                this.initPayment().then((data) => {
                    if (this.props.onInit) this.props.onInit(data.payment_id)
                    this.renderWidget(data)
                }),
            () => {
                const errorMessage = "Ошибка при соединении с сервисом оплаты"
                this.sendErrorToSentry(errorMessage)
                this.props.onError(errorMessage)
            }
        )
    }

    initPayment = (forceInit = false) => {
        const jsonData: JsonData = {
            payment_system: this.props.payment_system,
            save_payment_tool: this.props?.savePaymentTool || false,
        }

        if (this.props.booking_hash) {
            jsonData.force_init = forceInit
        }

        if (!this.props.initUrl) {
            void this.onError({ error: "Нет ссылки для инициализации" })?.finally(() =>
                this.destroyWidget()
            )
            throw new Error()
        }

        return httpClient.post<InitPaymentResponse>(this.props.initUrl, jsonData)
    }

    sendErrorToSentry = (error: string) => {
        SentryWrapper.getInstance().captureException(`YooKassaWidget error. ${error}. `, {
            tags: {
                bookingHash: this.props.booking_hash,
                clientId: this.props.client_id,
                orderId: this.props.order_id,
                bookingStatus: BookingStatusNameMap[this.props?.status as BookingStatus],
                hash_id: this.props.psyHashId,
            },
        })
    }

    onError = (error: YookassaWidgetError) => {
        if (error.error === "token_expired") {
            this.destroyWidget()
            return this.initPayment(true)
                .then((data) => this.renderWidget(data))
                .catch((e: string) => this.sendErrorToSentry(e))
        }

        this.sendErrorToSentry(stringify(error))
        this.props.onError(`Что-то пошло не так`)
    }

    renderWidget = (json: InitPaymentResponse) => {
        if (json?.error) {
            this.sendErrorToSentry(json.error)
            this.props.onError(json.error)
            return
        }
        this.props.onWidgetShown?.()

        const newReturnUrl = this.props.returnUrl ? new URL(this.props.returnUrl) : undefined

        if (newReturnUrl) newReturnUrl.searchParams.append("payment_id", json.payment_id)

        this.widget = new window.YooMoneyCheckoutWidget({
            confirmation_token: json?.confirmation_token,
            return_url: newReturnUrl ? newReturnUrl.toString() : "",
            error_callback: this.onError,
            customization: {
                colors: {
                    controlPrimary: "#2bc88b",
                    controlPrimaryContent: "#ffffff",
                },
            },
        })

        this.widget.on("complete", () => {
            this.props?.onAfterPayment && this.props.onAfterPayment()
            this.widget && this.widget.destroy()
        })

        this.widget.render(this.widgetRef.current?.id)
    }

    destroyWidget = () => {
        this.widget && this.widget.destroy()
        // Непонятно почему, но destroy не уничтожает весь виджет, лоадер остаётся, поэтому подчищаем тут вручную
        if (this.widgetRef.current) {
            this.widgetRef.current.innerHTML = ""
        }
    }
    render() {
        return <div id="yookassa-widget" ref={this.widgetRef} />
    }
}
